// src/components/SocialLoginButtons.jsx
import { useState } from 'react'
import api from '@/utils/axios'
import toast from 'react-hot-toast'
import { Loader2 } from 'lucide-react'

/**
 * Validates that a redirect URL belongs to the app's own API domain
 * to prevent open redirect attacks.
 */
function isAllowedRedirectUrl(url) {
  try {
    const parsed = new URL(url)
    const apiBase = import.meta.env.VITE_API_URL || window.location.origin
    const allowedOrigin = new URL(apiBase).origin
    return parsed.origin === allowedOrigin
  } catch {
    return false
  }
}

export default function SocialLoginButtons() {
  const [loading, setLoading] = useState(null)

  const handleSSO = async (provider) => {
    setLoading(provider)
    try {
      const { data } = await api.get(`/auth/social/${provider}/redirect`)
      const url = data.redirect_url || data.data?.redirect_url
      if (url && isAllowedRedirectUrl(url)) {
        window.location.href = url
      } else if (url) {
        console.error('[SocialLogin] Blocked redirect to untrusted URL:', url)
        toast.error('Redirect URL is not trusted. Please contact support.')
        setLoading(null)
      } else {
        toast.error(`No redirect URL received from ${provider}`)
        setLoading(null)
      }
    } catch (err) {
      const msg = err.response?.data?.message || `Failed to connect with ${provider}`
      toast.error(msg)
      setLoading(null)
    }
  }

  return (
    <div className="space-y-3">
      {/* Divider */}
      <div className="relative my-5">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-200" />
        </div>
        <div className="relative flex justify-center text-xs">
          <span className="bg-white px-3 text-gray-400 uppercase tracking-wider">or continue with</span>
        </div>
      </div>

      {/* Google */}
      <button
        onClick={() => handleSSO('google')}
        disabled={!!loading}
        className="w-full border border-gray-300 rounded-lg py-2.5 px-4 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-400 flex items-center justify-center gap-3 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {loading === 'google' ? (
          <Loader2 size={18} className="animate-spin text-gray-400" />
        ) : (
          <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
        )}
        Sign in with Google
      </button>

      {/* Microsoft */}
      <button
        onClick={() => handleSSO('microsoft')}
        disabled={!!loading}
        className="w-full border border-gray-300 rounded-lg py-2.5 px-4 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-400 flex items-center justify-center gap-3 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {loading === 'microsoft' ? (
          <Loader2 size={18} className="animate-spin text-gray-400" />
        ) : (
          <svg width="18" height="18" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
            <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
            <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
            <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
            <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
          </svg>
        )}
        Sign in with Microsoft
      </button>
    </div>
  )
}
